import { Injectable, Signal, signal, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, shareReplay, Subscription, tap } from 'rxjs';
import {
  IPosition,
  IPositionSearchRequest,
  IPositionSearchResponse,
  IPositionWithRelationsResponse,
} from '../models/position.model';
import { IPagination } from '../models';
import { AlertService, AlertType } from './alert.service';

@Injectable({ providedIn: 'root' })
export class PositionService {
  private alertService = inject(AlertService);
  private httpClient = inject(HttpClient);

  private urlBase = 'api/positions';
  private positions$: Observable<IPosition[]> | null = null;
  private positionsSubscription: Subscription | null = null;

  private _positions = signal<IPagination<IPositionSearchResponse> | undefined>(undefined);

  get positions(): Signal<IPagination<IPositionSearchResponse> | undefined> {
    return this._positions.asReadonly();
  }

  getAllPositions(): Observable<IPosition[]> {
    this.positions$ = this.positions$ ?? this.httpClient.get<IPosition[]>(this.urlBase).pipe(shareReplay(1));
    return this.positions$;
  }

  private bustCache(): void {
    this.positions$ = null;
  }

  fetchPositions({ params, payload }: { params: HttpParams; payload?: IPositionSearchRequest }) {
    this.positionsSubscription?.unsubscribe();
    this._positions.set(undefined);
    this.positionsSubscription = this.httpClient
      .post<IPagination<IPositionSearchResponse>>(`${this.urlBase}/search`, payload, { params })
      .subscribe({
        next: (data) => this._positions.set(data),
        error: (err) => this.alertService.showAlert(err, AlertType.error),
      });
  }

  createPosition(position: {
    title: string;
    departmentId: number | null;
    downwardPositionIds: number[];
  }): Observable<IPositionWithRelationsResponse> {
    return this.httpClient
      .post<IPositionWithRelationsResponse>(`${this.urlBase}/create`, position)
      .pipe(tap(() => this.bustCache()));
  }

  updateWithRelations(
    id: number,
    title: string,
    departmentId: number | null,
    downwardPositionIds: number[],
  ): Observable<IPositionWithRelationsResponse> {
    return this.httpClient
      .put<IPositionWithRelationsResponse>(`${this.urlBase}/update`, {
        id,
        title,
        departmentId,
        downwardPositionIds,
      })
      .pipe(tap(() => this.bustCache()));
  }

  deletePositionById(id: number): Observable<void> {
    return this.httpClient
      .delete<void>(`${this.urlBase}/${id}`)
      .pipe(tap(() => this.bustCache()));
  }
}
